import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useOutletContext, useParams } from 'react-router-dom'

import { Box, Form } from '@factorialco/deprecated-design-system'
import { zodResolver } from '@hookform/resolvers/zod'
import { CurrencySelectorCurrencyFragment } from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import FilesPreviewer from 'modules/expenses/components/expenses/Detail/_FilesPreviewer'
import NewPerDiemForm from 'modules/expenses/components/expenses/Detail/_Form/PerDiem/new'
import { getPerDiemFormDefaultValues } from 'modules/expenses/components/expenses/Detail/_Form/PerDiem/new/form/form'
import { PerDiemSchema } from 'modules/expenses/components/expenses/Detail/_Form/PerDiem/new/form/schema'
import Sidepanel from 'modules/expenses/components/expenses/Detail/_Sidepanel'
import { BulkActionsProvider } from 'modules/expenses/components/expenses/Detail/hooks/useBulkActions'
import { FilesPreviewerProvider } from 'modules/expenses/components/expenses/Detail/hooks/useFilesPreviewer'
import CreateExpenseFooterActions from 'modules/expenses/components/expenses/New/NewForm/CreateExpenseFooterActions'
import { normalizeEffectiveOn } from 'modules/expenses/hooks/useExpensable'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import useNewExpensesPage from 'modules/expenses/hooks/useNewExpensesPage'
import { useProjectsAssignedToEmployees } from 'modules/expenses/hooks/useProjects'
import type { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { filterCustomizationFieldsByExpenseDocumentType } from 'modules/expenses/lib/customization_settings/filterByExpenseDocumentType'
import { useBudgetOptions } from 'modules/finance/hooks/useBudgets'

export interface CreatePerDiemFormProps {
  customizationFields: CustomizationField[]
  hasProjectsFeature: boolean
  canSeeEmployeeLegalEntity: boolean
  currencies?: CurrencySelectorCurrencyFragment[]
  canCustomizeSettings: boolean
  hasReportOnBehalfFeature: boolean
  canCreateExpensesForOthers: boolean
  groupCurrency?: string | null
  onClose: () => void
}

const CreatePerDiemForm = ({
  customizationFields: unfilteredCustomizationFields,
  hasProjectsFeature,
  canSeeEmployeeLegalEntity,
  hasReportOnBehalfFeature,
  canCreateExpensesForOthers,
  groupCurrency,
  onClose,
}: CreatePerDiemFormProps) => {
  // TODO: Implement the effective on change based on per diem
  const [formEffectiveOn, setFormEffectiveOn] = useState('')
  const [formEmployeeId, setFormEmployeeId] = useState<number>()
  const [openSidepanel, toggleOpenSidepanel] = useToggle(true)
  const [_openFilePreviewer, toggleOpenFilePreviewer] = useToggle(true)
  const [documentType, setDocumentType] = useState<string | undefined>(undefined)

  const { employee } = useCurrentSession()
  const { loading, hasCostCentersFeature } = useNewExpensesPage()
  const { groupId } = useParams()
  const employeeId = formEmployeeId ?? employee?.id

  const { taxAgencyOrganizationId } = useOutletContext<RouteContextParams>()

  const customizationFields = useUnsafeMemo(
    () =>
      filterCustomizationFieldsByExpenseDocumentType(unfilteredCustomizationFields, documentType),
    [unfilteredCustomizationFields, documentType]
  )

  const defaultValues = getPerDiemFormDefaultValues({
    employee,
    customizationFields,
    groupId: groupId ? Number(groupId) : null,
    groupCurrency,
  })

  const { projectWorkers: newProjectWorkers, loading: newLoadingProjectWorkers } =
    useProjectsAssignedToEmployees({
      skip: hasProjectsFeature === false,
      employeeIds: employeeId ? [employeeId] : [],
    })

  const { budgetOptions: newBudgetOptions, loading: newLoadingBudgetOptions } = useBudgetOptions({
    effectiveOn: normalizeEffectiveOn(formEffectiveOn),
    employeeId,
  })

  const perDiemForm = useForm({
    mode: 'onChange',
    resolver: zodResolver(
      PerDiemSchema({
        customizationFields,
        budgetOptions: newBudgetOptions,
        projectWorkers: newProjectWorkers,
        hasProjectsFeature,
        hasCostCentersFeature,
        resourceType: 'perdiem',
      })
    ),
    defaultValues: {
      ...defaultValues,
      files: [],
    },
  })

  const { trigger } = perDiemForm

  useUnsafeEffect(() => {
    trigger()
  }, [customizationFields, trigger])

  const [onDropHandler, setOnDropHandler] = useState<(files: File[]) => void>(() => () => {})

  const handleClose = () => {
    toggleOpenSidepanel()
    onClose()
  }

  const handleCloseClicked = () => {
    toggleOpenFilePreviewer()
  }

  if (loading) return <Loading inline />

  return (
    <BulkActionsProvider>
      <Sidepanel
        open={openSidepanel}
        onClose={handleClose}
        onCloseClicked={handleCloseClicked}
        canRestartApprovalFlow={false}
      >
        <FilesPreviewerProvider initialFiles={[]}>
          <FilesPreviewer
            onClose={handleClose}
            onDrop={onDropHandler}
            showNewForm
            showUploadButton
            isFilesFieldEnabled={customizationFields.some(
              (field) => field.id === 'perdiem-files' && field.enabled
            )}
          />
          <Form padding="s0" notRounded>
            <FormProvider {...perDiemForm}>
              <Box flexGrow gap="s16" paddingX="s24">
                <NewPerDiemForm
                  customizationFields={customizationFields}
                  canSeeEmployeeLegalEntity={canSeeEmployeeLegalEntity}
                  canCreateExpensesForOthers={canCreateExpensesForOthers}
                  canEditExpensesWithPendingStatus={false}
                  hasProjectsFeature
                  hasReportOnBehalfFeature={hasReportOnBehalfFeature}
                  taxAgencyOrganization={null}
                  projectWorkers={newProjectWorkers}
                  budgetOptions={newBudgetOptions}
                  loadingProjectWorkers={newLoadingProjectWorkers}
                  loadingBudgetOptions={newLoadingBudgetOptions}
                  onEmployeeIdChange={setFormEmployeeId}
                  onEffectiveOnChange={() => {}}
                  registerDropHandler={setOnDropHandler}
                  hasCostCentersFeature={hasCostCentersFeature}
                />
              </Box>
              <CreateExpenseFooterActions
                formType="perdiem"
                customizationFields={customizationFields}
                onClose={handleClose}
                groupId={groupId}
                taxAgencyOrganization={taxAgencyOrganizationId}
              />
            </FormProvider>
          </Form>
        </FilesPreviewerProvider>
      </Sidepanel>
    </BulkActionsProvider>
  )
}

export default CreatePerDiemForm
